import axios from 'axios';
import { refreshTokenApi } from './auth';

const API_TIMEOUT_MS = parseInt(process.env.REACT_APP_API_TIMEOUT_MS || '60000', 10);
const computeBaseURL = () => {
  const env = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_PUBLIC_BASE_URL;
  if (env) return env as string;
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location as any;
    const p = port ? `:${port}` : '';
    return `${protocol}//${hostname}${p}`;
  }
  return '/';
};

const request = axios.create({
  baseURL: computeBaseURL(),
  timeout: API_TIMEOUT_MS,
  withCredentials: true,
});

let isRefreshing = false;
let refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;

// 请求拦截器:添加 JWT token
request.interceptors.request.use(
  config => {
    const token = localStorage.getItem('jwt_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // 开发环境下或强制开启时打印请求日志
    const isDevelopment = process.env.NODE_ENV === 'development' || localStorage.getItem('debug_api') === 'true';
    if (isDevelopment) {
      const method = config.method?.toUpperCase();
      const url = config.url;
      // 过滤敏感路径
      const sensitivePaths = ['/api/auth/login', '/api/auth/refresh'];
      const isSensitive = sensitivePaths.some(path => url?.includes(path));

      // console.log(`[API Request] ${method} ${url}`, {
      //   headers: { ...config.headers, Authorization: config.headers.Authorization ? '[REDACTED]' : undefined },
      //   params: config.params,
      //   data: config.data && !isSensitive ? config.data : '[FILTERED]'
      // });
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器:处理 401/403,token 刷新
request.interceptors.response.use(
  response => {
    // 开发环境下或强制开启时打印响应日志
    const isDevelopment = process.env.NODE_ENV === 'development' || localStorage.getItem('debug_api') === 'true';
    if (isDevelopment) {
      const method = response.config.method?.toUpperCase();
      const url = response.config.url;
      const status = response.status;
      // console.log(`[API Response] ${status} ${method} ${url}`, {
      //   status: response.status,
      //   data: response.data,
      //   duration: response.headers['x-response-time'] || 'N/A'
      // });
    }
    return response;
  },
  async error => {
    const status = error?.response?.status;
    const originalConfig = error?.config || {};
    const url = String(originalConfig?.url || '');

    // 记录API错误
    if (status && status >= 400) {
      console.error('[Request] API Error:', {
        status,
        url,
        method: originalConfig.method?.toUpperCase(),
        message: error?.response?.data?.message || error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }

    if (
      status === 401 &&
      !(originalConfig as any)._retry &&
      !url.includes('/api/auth/refresh') &&
      !url.includes('/api/auth/login')
    ) {
      // console.log('[Request] Attempting token refresh for 401 error');
      (originalConfig as any)._retry = true;
      const refresh = localStorage.getItem('jwt_refresh_token');
      if (!refresh) {
        // console.warn('[Request] No refresh token available, logging out');
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('jwt_refresh_token');
        // 同步其他标签页
        try { localStorage.setItem('jwt_token', ''); localStorage.removeItem('jwt_token'); } catch { }
        try { localStorage.setItem('jwt_refresh_token', ''); localStorage.removeItem('jwt_refresh_token'); } catch { }
        if (typeof window !== 'undefined' && (window as any).__logout) {
          try { (window as any).__logout(); } catch { }
        } else {
          try { window.location.href = '/login'; } catch { }
        }
        return Promise.reject(error);
      }
      try {
        if (!isRefreshing) {
          isRefreshing = true;
          // console.log('[Request] Starting token refresh process');
          refreshPromise = refreshTokenApi(refresh).finally(() => {
            isRefreshing = false;
          });
        }
        const { accessToken, refreshToken } = await (refreshPromise as Promise<{ accessToken: string; refreshToken: string }>);
        localStorage.setItem('jwt_token', accessToken);
        localStorage.setItem('jwt_refresh_token', refreshToken);
        // 通知其他标签页刷新凭据
        try { localStorage.setItem('jwt_token', accessToken); } catch { }
        try { localStorage.setItem('jwt_refresh_token', refreshToken); } catch { }
        originalConfig.headers = originalConfig.headers || {};
        originalConfig.headers.Authorization = `Bearer ${accessToken}`;
        // console.log('[Request] Token refresh successful, retrying request');
        return request(originalConfig);
      } catch (refreshError) {
        console.error('[Request] Token refresh failed:', refreshError);
        localStorage.removeItem('jwt_token');
        localStorage.removeItem('jwt_refresh_token');
        try { localStorage.setItem('jwt_token', ''); localStorage.removeItem('jwt_token'); } catch { }
        try { localStorage.setItem('jwt_refresh_token', ''); localStorage.removeItem('jwt_refresh_token'); } catch { }
        if (typeof window !== 'undefined' && (window as any).__logout) {
          try { (window as any).__logout(); } catch { }
        } else {
          try { window.location.href = '/login'; } catch { }
        }
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default request;
